<template>
  <div :class="['input-group', variantClass]">
    <input
      v-for="i in modelValue.length"
      :key="i"
      :ref="(el) => setInput(el, i - 1)"
      :value="modelValue[i - 1]"
      :disabled="disableInput"
      class="input-digit"
      type="text"
      inputmode="numeric"
      maxlength="1"
      @beforeinput.prevent
      @mousedown.prevent="inputs[i - 1]?.select()"
      @keydown="handleKey($event, i - 1)"
      @paste="handlePaste($event, i - 1)"
    >
  </div>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  PropType,
  ref,
} from 'vue';

const variantClasses = {
  default: '',
  error: 'error',
} as const;

type ComponentVariants = keyof typeof variantClasses;

export default defineComponent({
  props: {
    modelValue: {
      type: Array as PropType<string[]>,
      required: true,
    },
    disableInput: {
      type: Boolean,
      default: false,
    },
    autoFocus: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String as PropType<ComponentVariants>,
      default: 'default',
      validator(variant: string) {
        return Object.hasOwn(variantClasses, variant);
      },
    },
  },
  emits: ['update:modelValue', 'focusNext', 'interacted'],
  setup(props, { emit }) {
    const inputs = ref<HTMLInputElement[]>([]);

    const variantClass = computed(() => variantClasses[props.variant]);

    const setInput = (el: HTMLInputElement | null, index: number) => {
      if (el) {
        inputs.value[index] = el;
      } else {
        delete inputs.value[index];
      }
    };

    const moveTo = (index: number) => {
      if (index >= inputs.value.length) {
        emit('focusNext');
      } else {
        const input = inputs.value[index];
        input?.focus();
        input?.select();
      }
    };

    const emitReplace = (index: number, val: string) => {
      const newDigits = [...props.modelValue];
      newDigits[index] = val;
      emit('update:modelValue', newDigits);
    };

    const emitDelete = (index: number) => {
      const newDigits = [...props.modelValue];
      newDigits.push('');
      newDigits.splice(index, 1);
      emit('update:modelValue', newDigits);
    };

    const handleKey = (ev: KeyboardEvent, index: number) => {
      const { key } = ev;

      switch (key) {
        case 'ArrowLeft':
          moveTo(index - 1);
          break;

        case 'ArrowRight':
          moveTo(index + 1);
          break;

        case 'Delete':
          if (props.modelValue[index]) emitReplace(index, '');
          else {
            emitDelete(index);
            nextTick(() => moveTo(index));
          }
          break;

        case 'Backspace':
          if (props.modelValue[index]) emitReplace(index, '');
          else moveTo(index - 1);
          break;

        default:
          if (/^\d$/.test(key)) {
            emitReplace(index, key);
            moveTo(index + 1);
          } else {
            // evita o ev.preventDefault() que quebra navegação por tecla tab
            emit('interacted', { key, wasHandled: false });
            return;
          }
      }

      emit('interacted', { key, wasHandled: true });
      ev.preventDefault();
    };

    const handlePaste = (ev: ClipboardEvent, index: number) => {
      const pasteData = ev.clipboardData?.getData('text/plain')?.trim();
      if (!pasteData) return;

      let nextIndex = index;
      const newDigits = [...props.modelValue];

      const isFull = () => nextIndex >= newDigits.length;

      const insertDigit = (char: string) => {
        if (/\D/.test(char)) return;
        newDigits[nextIndex] = char;
        nextIndex += 1;
      };

      for (let i = 0; i < pasteData.length && !isFull(); i += 1) {
        insertDigit(pasteData[i]);
      }

      emit('update:modelValue', newDigits);

      if (isFull()) {
        emit('focusNext');
      } else {
        inputs.value[nextIndex]?.focus();
        inputs.value[nextIndex]?.select();
      }
    };

    onMounted(() => {
      if (props.autoFocus) inputs.value[0]?.focus();
    });

    return {
      inputs,
      variantClass,

      setInput,
      handleKey,
      handlePaste,
    };
  },
});
</script>

<style scoped lang="scss">
.input-group {
  display: flex;
  justify-content: center;
  gap: 1rem;

  .input-digit {
    font-size: 1.25rem;
    width: 2em;
    height: 2em;
    padding: 8px;
    text-align: center;
    border-radius: 4px;
    border: 1px solid #B0B4C5;

    @include responsive($tablet) {
      font-size: 1.4rem;
    }
  }

  &.error .input-digit {
    border-color: #D71F1E;
  }
}
</style>
